import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { addDays, getSeasonEnd, getSeasonStart, getTodayString } from '../../utils/dateUtils';

// Range state is URL-search-param backed so a view (with its dates) can be
// bookmarked or shared and survives refresh.

export interface AnalysisRange {
  from: string;
  to: string;
}

export type RangePreset =
  | 'last30'
  | 'thisMonth'
  | 'seasonToDate'
  | 'lastSeason'
  | 'thisYear'
  | 'custom';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function presetRange(preset: RangePreset): AnalysisRange {
  const today = getTodayString();
  const year = Number(today.slice(0, 4));
  switch (preset) {
    case 'thisMonth':
      return { from: today.slice(0, 7) + '-01', to: today };
    case 'seasonToDate': {
      const start = getSeasonStart(year);
      return { from: start <= today ? start : getSeasonStart(year - 1), to: today };
    }
    case 'lastSeason':
      return { from: getSeasonStart(year - 1), to: getSeasonEnd(year - 1) };
    case 'thisYear':
      return { from: `${year}-01-01`, to: today };
    case 'last30':
    case 'custom':
    default:
      return { from: addDays(today, -29), to: today };
  }
}

export interface AnalysisRangeState {
  range: AnalysisRange;
  setRange: (range: AnalysisRange) => void;
}

export function useAnalysisRange(): AnalysisRangeState {
  const [searchParams, setSearchParams] = useSearchParams();

  const range = useMemo<AnalysisRange>(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to && DATE_RE.test(from) && DATE_RE.test(to) && from <= to) {
      return { from, to };
    }
    return presetRange('last30');
  }, [searchParams]);

  const setRange = useCallback((next: AnalysisRange) => {
    if (!DATE_RE.test(next.from) || !DATE_RE.test(next.to) || next.from > next.to) return;
    setSearchParams(params => {
      const merged = new URLSearchParams(params);
      merged.set('from', next.from);
      merged.set('to', next.to);
      return merged;
    }, { replace: true });
  }, [setSearchParams]);

  return { range, setRange };
}
